import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, Save, X, Upload, Sparkles, Image as ImageIcon, Eye, EyeOff } from "lucide-react";

function FileUploader({ onUploaded, label = "Upload Image" }) {
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) { toast.error("Image too large (max 15MB)"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(r.data.url);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <label className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider-cap cursor-pointer text-foreground/80 hover:text-red-wine border border-border rounded-md px-3 py-1.5 hover:bg-white/50">
      <Upload className="w-3.5 h-3.5"/> {busy ? "Uploading…" : label}
      <input type="file" accept="image/*" onChange={handle} className="hidden" disabled={busy}/>
    </label>
  );
}

const blank = { title: "", tagline: "", description: "", image: "", cta_label: "Learn More", cta_url: "", badge: "", sort_order: 0, enabled: true };

function NewForm({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setBusy(true);
    try {
      await api.post("/admin/featured-products", form);
      toast.success("Product added");
      setForm(blank);
      setOpen(false);
      onCreated();
    } catch { toast.error("Could not save"); }
    finally { setBusy(false); }
  };

  if (!open) return (
    <button onClick={() => setOpen(true)}
      className="w-full faith-card p-6 border-2 border-dashed border-[#F39A1F]/60 hover:border-[#F39A1F] hover:bg-white/50 transition flex items-center justify-center gap-3 text-red-wine font-bold uppercase tracking-wider-cap"
      data-testid="open-new-product-btn">
      <Plus className="w-5 h-5"/> Add A Featured Product
    </button>
  );

  return (
    <form onSubmit={submit} className="faith-card p-6 space-y-4" data-testid="new-product-form">
      <div className="flex items-center justify-between">
        <h3 className="font-serif-display text-xl text-red-wine uppercase">New Featured Product</h3>
        <button type="button" onClick={() => setOpen(false)} className="text-foreground/60 hover:text-red-wine"><X className="w-5 h-5"/></button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider-cap font-bold block mb-1">Title *</label>
          <input required maxLength={140} className="w-full px-3 py-2 rounded-md border border-border bg-white text-sm"
            placeholder='e.g. "With Faith All Things Are Possible"'
            value={form.title} onChange={e=>setForm({...form, title:e.target.value})} data-testid="new-product-title"/>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider-cap font-bold block mb-1">Eyebrow Tagline</label>
          <input maxLength={200} className="w-full px-3 py-2 rounded-md border border-border bg-white text-sm"
            placeholder="From Mike · Available Now"
            value={form.tagline} onChange={e=>setForm({...form, tagline:e.target.value})}/>
        </div>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-wider-cap font-bold block mb-1">Description</label>
        <textarea rows={2} maxLength={1000} className="w-full px-3 py-2 rounded-md border border-border bg-white text-sm"
          placeholder="A short paragraph members will see under the title."
          value={form.description} onChange={e=>setForm({...form, description:e.target.value})}/>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-wider-cap font-bold block mb-1">Product Image / 3D Mockup</label>
        <div className="flex items-center gap-3 flex-wrap">
          <input className="flex-1 min-w-0 px-3 py-2 rounded-md border border-border bg-white text-sm"
            placeholder="Image URL (or upload)" value={form.image} onChange={e=>setForm({...form, image:e.target.value})}/>
          <FileUploader onUploaded={(url) => setForm(f => ({...f, image: url}))} label="Upload"/>
          {form.image && <img src={form.image} alt="" className="h-12 w-16 object-contain rounded border border-border bg-white"/>}
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider-cap font-bold block mb-1">Button Label</label>
          <input maxLength={40} className="w-full px-3 py-2 rounded-md border border-border bg-white text-sm"
            placeholder="Buy Now / Learn More / Get Yours"
            value={form.cta_label} onChange={e=>setForm({...form, cta_label:e.target.value})}/>
        </div>
        <div className="sm:col-span-2">
          <label className="text-[10px] uppercase tracking-wider-cap font-bold block mb-1">Button Link (sales page)</label>
          <input maxLength={500} className="w-full px-3 py-2 rounded-md border border-border bg-white text-sm"
            placeholder="https://your-funnel.com  OR  /books"
            value={form.cta_url} onChange={e=>setForm({...form, cta_url:e.target.value})} data-testid="new-product-cta-url"/>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider-cap font-bold block mb-1">Badge (optional eyebrow on image)</label>
          <input maxLength={30} className="w-full px-3 py-2 rounded-md border border-border bg-white text-sm"
            placeholder='e.g. "NEW", "COMING SOON", "BESTSELLER"'
            value={form.badge} onChange={e=>setForm({...form, badge:e.target.value})}/>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider-cap font-bold block mb-1">Sort Order (0 = first)</label>
          <input type="number" className="w-full px-3 py-2 rounded-md border border-border bg-white text-sm"
            value={form.sort_order} onChange={e=>setForm({...form, sort_order:parseInt(e.target.value)||0})}/>
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-2 border-t border-border/40">
        <button type="button" onClick={() => setOpen(false)} className="btn-outline-gold !py-2 !px-4 !text-xs">Cancel</button>
        <button type="submit" disabled={busy} className="btn-primary inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="new-product-submit">
          <Save className="w-4 h-4"/> {busy ? "Saving…" : "Add Product"}
        </button>
      </div>
    </form>
  );
}

function Row({ p, onChanged }) {
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({...p});

  const save = async () => {
    try {
      await api.put(`/admin/featured-products/${p.id}`, form);
      toast.success("Saved");
      setEdit(false);
      onChanged();
    } catch { toast.error("Could not save"); }
  };

  const remove = async () => {
    if (!confirm(`Delete "${p.title}"?`)) return;
    try {
      await api.delete(`/admin/featured-products/${p.id}`);
      toast.success("Deleted");
      onChanged();
    } catch { toast.error("Could not delete"); }
  };

  const toggleEnabled = async () => {
    try {
      await api.put(`/admin/featured-products/${p.id}`, { enabled: !p.enabled });
      onChanged();
    } catch { toast.error("Could not toggle"); }
  };

  return (
    <div className={`faith-card p-4 ${!p.enabled ? "opacity-60" : ""}`} data-testid={`product-row-${p.id}`}>
      {edit ? (
        <div className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <input className="w-full px-3 py-2 rounded-md border border-border bg-white text-sm" value={form.title} onChange={e=>setForm({...form, title:e.target.value})} placeholder="Title *"/>
            <input className="w-full px-3 py-2 rounded-md border border-border bg-white text-sm" value={form.tagline} onChange={e=>setForm({...form, tagline:e.target.value})} placeholder="Tagline"/>
          </div>
          <textarea rows={2} className="w-full px-3 py-2 rounded-md border border-border bg-white text-sm" value={form.description} onChange={e=>setForm({...form, description:e.target.value})} placeholder="Description"/>
          <div className="flex items-center gap-3 flex-wrap">
            <input className="flex-1 min-w-0 px-3 py-2 rounded-md border border-border bg-white text-sm" value={form.image} onChange={e=>setForm({...form, image:e.target.value})} placeholder="Image URL"/>
            <FileUploader onUploaded={(url) => setForm(f => ({...f, image: url}))}/>
            {form.image && <img src={form.image} alt="" className="h-12 w-16 object-contain rounded border border-border bg-white"/>}
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <input className="w-full px-3 py-2 rounded-md border border-border bg-white text-sm" value={form.cta_label} onChange={e=>setForm({...form, cta_label:e.target.value})} placeholder="Button label"/>
            <input className="sm:col-span-2 w-full px-3 py-2 rounded-md border border-border bg-white text-sm" value={form.cta_url} onChange={e=>setForm({...form, cta_url:e.target.value})} placeholder="Button URL"/>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <input className="w-full px-3 py-2 rounded-md border border-border bg-white text-sm" value={form.badge} onChange={e=>setForm({...form, badge:e.target.value})} placeholder='Badge (e.g. "NEW")'/>
            <input type="number" className="w-full px-3 py-2 rounded-md border border-border bg-white text-sm" value={form.sort_order} onChange={e=>setForm({...form, sort_order:parseInt(e.target.value)||0})} placeholder="Sort order"/>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={remove} className="text-red-wine hover:text-[#1F8A2A] py-1.5 px-3 text-xs uppercase tracking-wider-cap font-bold inline-flex items-center gap-1.5"><Trash2 className="w-3.5 h-3.5"/> Delete</button>
            <button onClick={() => setEdit(false)} className="btn-outline-gold !py-1.5 !px-3 !text-xs">Cancel</button>
            <button onClick={save} className="btn-primary !py-1.5 !px-3 !text-xs inline-flex items-center gap-1.5"><Save className="w-3.5 h-3.5"/> Save</button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-4">
          {p.image ? <img src={p.image} alt="" className="h-16 w-20 object-contain rounded border border-border bg-white shrink-0"/> :
            <div className="h-16 w-20 rounded border border-border bg-white flex items-center justify-center shrink-0"><ImageIcon className="w-5 h-5 text-foreground/40"/></div>}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {p.badge && <span className="text-[9px] bg-[#2E9F3A] text-white px-1.5 py-0.5 rounded tracking-wider-cap font-bold">{p.badge}</span>}
              <div className="font-bold text-sm text-foreground truncate">{p.title}</div>
              {!p.enabled && <span className="text-[9px] text-foreground/60 italic">(hidden)</span>}
            </div>
            <div className="text-[11px] text-foreground/60 truncate mt-0.5">
              {p.tagline} {p.cta_url && <span>→ {p.cta_url}</span>}
            </div>
          </div>
          <button onClick={toggleEnabled} title={p.enabled ? "Hide from members" : "Show to members"} className="p-1.5 text-foreground/70 hover:text-red-wine">
            {p.enabled ? <Eye className="w-4 h-4"/> : <EyeOff className="w-4 h-4"/>}
          </button>
          <button onClick={() => setEdit(true)} className="p-1.5 text-foreground/70 hover:text-red-wine"><Edit3 className="w-4 h-4"/></button>
        </div>
      )}
    </div>
  );
}

export default function FeaturedProductsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/featured-products");
      setItems(r.data.products || []);
      setErr(null);
    } catch (e) {
      setErr(e.response?.status === 403 ? "Admin only" : "Could not load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="text-center py-10 text-foreground/60">Loading…</div>;
  if (err === "Admin only") return (
    <div className="faith-card p-8 text-center">
      <div className="text-2xl text-red-wine font-serif-display uppercase">Admin Only</div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="featured-products-tab">
      <div>
        <div className="text-xs tracking-wider-cap text-gold-deep">★ Featured Products · Admin · Mike's Tool</div>
        <h2 className="text-3xl uppercase text-red-wine">Curate The Member Back Office</h2>
        <p className="text-foreground/85 mt-2 max-w-2xl text-base">
          Add a product card → it shows up at the top of every member's back office with a clickable button to your sales page (a funnel, an external URL, anything).
          Sort order, badges (NEW / COMING SOON / BESTSELLER), and visibility toggle all controlled here.
        </p>
      </div>

      <NewForm onCreated={load}/>

      {items.length === 0 ? (
        <div className="text-center text-foreground/70 py-10 faith-card bg-white">
          <Sparkles className="w-10 h-10 text-[#F39A1F]/60 mx-auto mb-2"/>
          <div className="font-bold uppercase tracking-wider-cap text-sm">No featured products yet</div>
          <p className="text-foreground/60 text-sm mt-1">Click "Add A Featured Product" above. Members will only see this section once you add at least one.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="featured-products-list">
          {items.map(p => <Row key={p.id} p={p} onChanged={load}/>)}
        </div>
      )}
    </div>
  );
}
